import {ref} from "vue"
import { useRouter } from 'vue-router'
import {user} from "@/functions/user";
import getHCaptcha from "@/functions/getHCaptcha";
import getLoading from "@/functions/getLoading";
import getToastAlert from "@/functions/getToastAlert";
import LoggedIn from "@/functions/LoggedIn";
import {get, parseRequestOptionsFromJSON} from "@github/webauthn-json/browser-ponyfill";
import getAppYear from "@/functions/getAppYear";
import getAppName from "@/functions/getAppName";
import {withTraceparent} from "@/functions/getTraceparent";
import {signalUnknownCredential} from "@/functions/signalCredentials";
import {passkeyLoginFinishApi, passkeyLoginStartApi, readPasskeyFinish, readPasskeyStart} from "@/functions/passkeyLoginRead";
import {loginApi, loginRequestBody, readLoginDispatch} from "@/functions/loginRead";

export const isLoginComponentActive = ref(true);
export const rejectTimeout = ref(null);

const getAuthenticate = () => {
    const { callCaptcha } = getHCaptcha()
    const { showLoading, hideLoading } = getLoading()
    const {displayDefaultError, displayToastAlert, hideToastAlert, display429Error} = getToastAlert()
    const { handleSuccessfulAuth } = LoggedIn()
    const router = useRouter()
    const username = ref('')
    const userPassword = ref('')

    const credential = ref()
    const credentialRequestOptions = ref()
	let abortController = ref(null)
	let timeoutWorker = ref(null)

	const initTimeoutWorker = () => {
		if (!timeoutWorker.value) {
			timeoutWorker.value = new Worker('/js/passkeyTimeoutWorker.js');
			timeoutWorker.value.addEventListener('message', (e) => {
				if (e.data.type === 'timeout' && rejectTimeout.value) {
					rejectTimeout.value();
				}
			});
		}
	};

	const cleanupPasskeyLogin = () => {
		isLoginComponentActive.value = false;
		if (rejectTimeout.value) {
			rejectTimeout.value();
			rejectTimeout.value = null;
		}
		if (timeoutWorker.value) {
			timeoutWorker.value.terminate();
			timeoutWorker.value = null;
		}
	};

    const authenticate = async () => {
        showLoading()

        if (userPassword.value.length > 100) {
            displayToastAlert({
                "type": "ERROR",
                "title": "Incorrect Username or Password",
                "message": "Try entering your <b>Username</b> and <b>Password</b> again. Remember that passwords are case-sensitive."
            })
            hideLoading()
            return
        }

        let isValidCaptcha = await callCaptcha('captcha-login')

        if (isValidCaptcha) {
	        let isFreeFile = !!sessionStorage.getItem('isFreeFile')

            await fetch(loginApi, {
                method: 'post',
                headers: withTraceparent({
                    'content-type': 'application/json',
                }),
                body: JSON.stringify(loginRequestBody({
                    username: username.value,
                    password: userPassword.value,
                    captcha: user.captcha,
                    fingerprint: sessionStorage.getItem('validateId'),
                    appYear: getAppYear(),
                    appName: getAppName(),
                    redirect_uri: sessionStorage.getItem('redirect_uri'),
                    client_id: sessionStorage.getItem('client_id'),
                    state: sessionStorage.getItem('state'),
                    platform: user.platform,
                    isFreeFile: isFreeFile,
                }))
            }).then(res => {
	            if (res.status && res.status === 429) throw 429;
	            return res.json();
            }).then(data => {
				data.passwordSignIn = true;
				handleAuthResponse(data)
            })
            .catch((err) => {
	            if (err === 429) {
		            display429Error()
		            hideLoading()
	            }
				else {
		            displayDefaultError()
	            }
            })
        }
        else {
            await displayDefaultError()
            hideLoading()
        }
    }

    const callPasskeyLoginFinish = async () => {
        let isValidCaptcha = await callCaptcha('captcha-login')

        if (isValidCaptcha) {
            await fetch(passkeyLoginFinishApi, {
                method: 'post',
                headers: withTraceparent({
                    'content-type': 'application/json',
                }),
                body: JSON.stringify({
                    "credential": JSON.stringify(credential.value),
	                "fingerprint": sessionStorage.getItem('validateId'),
	                "appYear": getAppYear(),
	                "appName": getAppName(),
                    "captcha": user.captcha,
	                "redirect_uri": sessionStorage.getItem('redirect_uri'),
	                "client_id": sessionStorage.getItem('client_id'),
	                "state": sessionStorage.getItem('state'),
                    "platform": user.platform,
                })
            }).then(res => {
	            if (res.status && res.status === 429) throw 429;
                return res.json();
            }).then(async data => {
	            const read = readPasskeyFinish(data)
	            if (!read.alert) {
					data.passwordSignIn = false;
		            handleAuthResponse(data);
	            } else {
		            displayToastAlert(read.alert)
		            await signalUnknownCredential(read.UnknownCredentialOptions)
		            await callPasskeyLoginStart()
	            }
            }).catch((err) => {
	            if (err === 429) {
		            display429Error()
		            hideLoading()
	            }
	            else {
		            displayDefaultError()
	            }
            })
        } else {
            await displayDefaultError()
        }
    }

    const callPasskeyLoginStart = async (mediation = 'conditional') => {
	    abortController.value?.abort(); //ensure only one request at a time
	    abortController.value = new AbortController()
	    initTimeoutWorker();

        const read = readPasskeyStart(await fetch(passkeyLoginStartApi).then(res => res.json()))
        const publicKey = read.publicKey
	    const timeoutDuration = read.timeout;
	    credentialRequestOptions.value = {
            mediation,
            publicKey,
            signal: abortController.value.signal
        }

        const parsedOptions = parseRequestOptionsFromJSON(credentialRequestOptions.value)

	    const timeoutPromise = new Promise((_, reject) => {
		    rejectTimeout.value = () => {
			    abortController.value.abort("timeout");
			    timeoutWorker.value?.postMessage({ action: 'cancel' });
			    reject(new Error("timeout"));
		    };

		    timeoutWorker.value.postMessage({
			    action: 'start',
			    duration: timeoutDuration
		    });
	    });

	    try {
		    const credentialResponse = await Promise.race([
			    get(parsedOptions),
			    timeoutPromise
		    ]);

		    timeoutWorker.value?.postMessage({ action: 'cancel' }); // Ensure timeout is canceled on success
		    credential.value = credentialResponse;
		    await callPasskeyLoginFinish();
	    } catch (error) {
		    const signal = parsedOptions.signal
		    if (isLoginComponentActive.value && ((signal.aborted && signal.reason === "timeout") || error.message.includes("timeout"))) {
			    console.log("timeout, restarting login")
				await callPasskeyLoginStart(mediation);
		    }
	    }
    }

    const callPasskeyLoginModal = async () => {
	    await callPasskeyLoginStart('optional')
    }

    const checkIsPasskeyLoginAvailable = async () => {
	    if (window.PublicKeyCredential && PublicKeyCredential.isConditionalMediationAvailable) {
		    const isCMA = await PublicKeyCredential.isConditionalMediationAvailable()
		    if (isCMA) {
			    await callPasskeyLoginStart()
		    }
	    }
    }

	const handleAuthResponse = (data) => {
		const read = readLoginDispatch(data)
		if (read.verificationCode) {
			sessionStorage.setItem('verifyCodeTest', read.verificationCode);
		}
		if (read.authenticated) {
			hideToastAlert();
			abortController.value?.abort();
			handleSuccessfulAuth(data);
		}
		else if (read.reason === 'TWO_FACTOR_REQUIRED') {
			let methods = read.methods.toString();
			sessionStorage.setItem('methods', methods);
			sessionStorage.setItem('multipleNumbers', 'false');
			sessionStorage.setItem('phoneNumbers', '');

			if (read.phoneNumbers.length > 0) {
				if (read.phoneNumbers.length > 1) {
					sessionStorage.setItem('multipleNumbers', 'true');
				}
				sessionStorage.setItem('phoneNumbers', JSON.stringify(read.phoneNumbers));
			}

			if (read.defaultMethod !== 'NONE' || read.sentNumber) {
				let pageName;
				if (read.defaultMethod === 'EMAIL')
				{
					pageName = 'EmailVerification'
				}
				else if (read.defaultMethod === 'VOICE' || read.defaultMethod === 'TEXT')
				{
					pageName = 'TextVerification'
				}
				else if (read.defaultMethod === 'TOTP')
				{
					if (['EMAIL','VOICE','TEXT'].some(v => read.methods.includes(v))) {
						pageName = 'VerifyWithTwoStepMethod'
					}
					else {
						pageName = 'AuthAppVerification'
					}
				}
				else
				{
					displayDefaultError()
				}
				user.verifyTwoFactorData = JSON.stringify(data)
				router.push({name: pageName});
			} else {
				user.verifyTwoFactorData = JSON.stringify(data)
				router.push({name: 'VerifyWithTwoStepMethod'});
			}
		}
		else {
			displayToastAlert(read.alert);
			hideLoading();
		}
	};

	// Handle autologin for dev/test environments
	const handleAutoLogin = () => {
		const serverType = process.env.VUE_APP_ENV_NAME

		if (serverType === 'test' || serverType === 'dev' || serverType === 'stage') {
			const params = new URLSearchParams(window.location.search)
			const autologin = params.get('autologin')
			const usernameFromParams = params.get('username')
			const passwordFromParams = params.get('password')

			if (autologin && usernameFromParams && passwordFromParams) {
				showLoading()
				username.value = usernameFromParams;
				userPassword.value = passwordFromParams;
				setTimeout(authenticate, 1000)
			}
		}
	}

	return {
			username,
			userPassword,
			abortController,
			authenticate,
			callPasskeyLoginStart,
			callPasskeyLoginModal,
			callPasskeyLoginFinish,
			checkIsPasskeyLoginAvailable,
			cleanupPasskeyLogin,
			handleAutoLogin,
			handleAuthResponse
  }
}

export default getAuthenticate